import api from 'api';
import { get } from 'utils/fetch';
import { isPhoneSize } from 'utils/util';
import { GlobalContext } from 'GlobalContext';
import { useEffect, useState, useContext, useRef } from 'react';
import { Select, Space, message, Spin, Switch } from 'antd';
import { PlusCircleFilled, MinusCircleFilled } from '@ant-design/icons';
import styles from './XFeed.css';

export function XFeed() {
    const { manageRecordId, accessToken, refreshSession } =
        useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [twitterLoading, setTwitterLoading] = useState(true);
    const [isFeedInputOpen, setIsFeedInputOpen] = useState(true);
    const [curFeed, setCurFeed] = useState(null);
    const [isRotating, setIsRotating] = useState(false);
    const buttonRef = useRef(null);
    const containerRef = useRef(null);
    const [feedEntryToDelete, setFeedEntryToDelete] = useState(null);

    function DeleteConfirmation() {
        useEffect(() => {
            const loadTwitterScript = () => {
                if (!window.twttr) {
                    const script = document.createElement('script');
                    script.src = 'https://platform.twitter.com/widgets.js';
                    script.async = true;
                    script.onload = () =>
                        window.twttr?.widgets?.load(containerRef.current);
                    document.body.appendChild(script);
                } else {
                    window.twttr.widgets.load(containerRef.current);
                }
                setTwitterLoading(false);
            };

            loadTwitterScript();
        }, []);

        const deleteFeed = async (id) => {
            setLoading(true);
            try {
                const response = await fetch(api.offering.deleteFeed(id), {
                    method: 'DELETE',
                    body: JSON.stringify({}),
                    headers: {
                        Authorization: `Bearer ${accessToken}`, // Ensure authentication
                    },
                });
                refreshSession();
                if (!response.ok) throw new Error('Delete failed.');
                setFeedEntryToDelete(null);
                const data = await response.json();
                message.success('Feed deleted successfully!');
            } catch (error) {
                displayErrorMessage('Failed to delete Feed.');
            }
            fetchFeeds(manageRecordId);
        };

        return (
            <div
                style={{
                    position: 'fixed',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    width: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '10px',
                    border: '1px solid black',
                    zIndex: 1,
                }}
            >
                <div
                    style={{
                        fontSize: '20px',
                        margin: '10px',
                        marginBottom: '0',
                    }}
                >
                    Do you want to delete the feed?
                </div>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: feedEntryToDelete.embed,
                        }}
                        style={{ width: '300px' }}
                    />
                </div>
                <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                    <button
                        style={{
                            height: '30px',
                            width: '100px',
                            borderRadius: '30px',
                            background: 'rgb(17 32 86)',
                            color: 'white',
                            cursor: 'pointer',
                            margin: '10px',
                            marginTop: '0',
                        }}
                        onClick={() => {
                            deleteFeed(feedEntryToDelete.id);
                        }}
                    >
                        {'Delete'}
                    </button>
                    <button
                        style={{
                            height: '30px',
                            width: '100px',
                            borderRadius: '30px',
                            background: 'rgb(17 32 86)',
                            color: 'white',
                            cursor: 'pointer',
                            margin: '10px',
                            marginTop: '0',
                        }}
                        onClick={() => setFeedEntryToDelete(null)}
                    >
                        {'Cancel'}
                    </button>
                </div>
            </div>
        );
    }

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchFeeds(id) {
        await get(
            api.offering.getFeeds(id),
            setFeeds,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    useEffect(() => {
        // if (accessToken === "") return;
        fetchFeeds(manageRecordId);
    }, [accessToken]);

    const uploadFeed = async (event) => {
        if (!curFeed) return;
        setLoading(true);
        try {
            const response = await fetch(
                api.offering.uploadFeed(manageRecordId),
                {
                    method: 'POST',
                    body: JSON.stringify({
                        curFeed,
                    }),
                    headers: {
                        Authorization: `Bearer ${accessToken}`, // Ensure authentication
                    },
                }
            );
            refreshSession();
            if (!response.ok) throw new Error('Upload failed.');
            const data = await response.json();
            message.success('New feed uploaded successfully!');
            setCurFeed(null);
        } catch (error) {
            displayErrorMessage('Failed to upload Feed.');
        }
        fetchFeeds(manageRecordId);
    };

    const handleToggleFeedInput = () => {
        setIsRotating(true);
        setTimeout(() => {
            setIsFeedInputOpen((prev) => !prev);
        }, 150); // match your animation duration
        setTimeout(() => {
            setIsRotating(false);
        }, 150);
    };

    // Load Twitter script and rerender widgets when feeds or curFeed changes
    useEffect(() => {
        const loadTwitterScript = () => {
            if (!window.twttr) {
                const script = document.createElement('script');
                script.src = 'https://platform.twitter.com/widgets.js';
                script.async = true;
                script.onload = () =>
                    window.twttr?.widgets?.load(containerRef.current);
                document.body.appendChild(script);
            } else {
                window.twttr.widgets.load(containerRef.current);
            }
            setTwitterLoading(false);
        };

        loadTwitterScript();
    }, [feeds, curFeed]);

    return (
        <div style={{ justifyItems: 'center' }}>
            <div
                style={
                    isPhoneSize()
                        ? {
                              width: window.innerWidth - 20,
                          }
                        : {
                              width: '560px',
                          }
                }
            >
                <h1>Feed</h1>
                {feedEntryToDelete && <DeleteConfirmation />}
                {(loading || twitterLoading) && (
                    <div style={{ marginTop: '30px', textAlign: 'center' }}>
                        <Spin />
                    </div>
                )}

                {!loading && !twitterLoading && (
                    <>
                        {curFeed && (
                            <div
                                dangerouslySetInnerHTML={{ __html: curFeed }}
                                style={{ marginTop: '20px', opacity: 0.7 }}
                            />
                        )}

                        <textarea
                            // ref={textareaRef}
                            value={curFeed || ''}
                            onChange={(e) => setCurFeed(e.target.value)}
                            style={{
                                width: '100%',
                                height: isFeedInputOpen ? '60px' : '0px',
                                padding: isFeedInputOpen ? '10px' : '0px',
                                border: isFeedInputOpen
                                    ? '1px solid #ccc'
                                    : 'none',
                                borderRadius: '5px',
                                fontSize: '16px',
                                fontFamily: 'Arial, sans-serif',
                                resize: 'none', // Disable manual resizing
                                overflow: 'hidden', // Prevent scrollbars
                                transition: 'all 0.3s ease',
                            }}
                        />

                        <div
                            ref={buttonRef}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginTop: '20px',
                                marginBottom: '20px',
                            }}
                        >
                            <button
                                style={{
                                    height: '30px',
                                    width: '100px',
                                    borderRadius: '30px',
                                    background: 'rgb(17 32 86)',
                                    color: 'white',
                                    cursor: 'pointer',
                                }}
                                onClick={uploadFeed}
                            >
                                {'Add Feed'}
                            </button>
                        </div>

                        {feeds &&
                            feeds.map((feed, idx) => {
                                return (
                                    <div
                                        key={idx}
                                        style={{
                                            position: 'relative',
                                            marginBottom: '20px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            backgroundColor: '#fff',
                                            boxShadow:
                                                '0 2px 6px rgba(0, 0, 0, 0.05)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.querySelector(
                                                '.delete-btn'
                                            ).style.opacity = 1;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.querySelector(
                                                '.delete-btn'
                                            ).style.opacity = 0;
                                        }}
                                    >
                                        <button
                                            onClick={() => {
                                                setFeedEntryToDelete(feed);
                                            }}
                                            className="delete-btn"
                                            style={{
                                                position: 'absolute',
                                                top: '8px',
                                                right: '8px',
                                                background: '#f8f9fa',
                                                border: '1px solid #ccc',
                                                borderRadius: '50%',
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                width: '24px',
                                                height: '24px',
                                                cursor: 'pointer',
                                                opacity: 0,
                                                transition: 'opacity 0.2s ease',
                                                boxShadow:
                                                    '0 1px 3px rgba(0, 0, 0, 0.1)',
                                                color: '#444',
                                            }}
                                            title="Delete this feed"
                                        >
                                            ×
                                        </button>

                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: feed.embed,
                                            }}
                                        />
                                    </div>
                                );
                            })}
                    </>
                )}
            </div>
        </div>
    );
}
